import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A picture over the whole app, as the video tile shows its camera (tiles spec TB-1), for the image
 * tile (image-tile.md IM-6): black around it, clear of the status bar and the camera island; on an
 * upright window a landscape picture is turned a quarter to the right, to fill it when the phone is
 * turned. A tap anywhere or Escape closes it.
 */
export function FullScreenLayer({ label, isLandscape, onClose, children }: { label: string; isLandscape: boolean; onClose: () => void; children: ReactNode }) : ReactNode {
    const [isTurned, setTurned] = useState(false);

    useLayoutEffect(() => {
        const update = () : void => {
            setTurned(isLandscape && window.innerHeight > window.innerWidth);
        };

        update();
        window.addEventListener('resize', update);

        return () => window.removeEventListener('resize', update);
    }, [isLandscape]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) : void => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div
            role="button"
            aria-label={`${label}, full screen: tap to close`}
            data-full-screen
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                boxSizing: 'border-box',
                padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
                background: '#000000',
                cursor: 'zoom-out',
            }}
            onClick={onClose}
        >
            {/* The safe area, sized for the turned picture's box (container query units). */}
            <div style={{ position: 'relative', width: '100%', height: '100%', containerType: 'size' }}>
                <div
                    style={
                        isTurned
                            ? { position: 'absolute', left: '50%', top: '50%', width: '100cqh', height: '100cqw', transform: 'translate(-50%, -50%) rotate(90deg)' }
                            : { position: 'absolute', inset: 0 }
                    }
                >
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    );
}
